import React, { useMemo } from 'react';

import { NavLinkButton } from '@shared/components/bootstrap';

import { Formatter as PhoneFormatter } from '@admin/components/phone/formatter';
import { Spinner } from '@admin/components/spinner';
import { client } from '@admin/libraries/apollo';
import { useCustomersPanelAccountQuery, type CustomersPanelAccountQuery } from '@admin/schema';

/** Props for the CustomersPanel component. */
interface ICustomersPanelProps {
  /** The account ID to display customer information for. */
  accountID: string;
}

type CustomersPanelAccount = NonNullable<CustomersPanelAccountQuery['account']>;
type ICustomer = CustomersPanelAccount['customers'][number];
type ICustomerPhone = NonNullable<ICustomer['customerPhones']>[number];

/**
 * Sorts an array with the primary item first, then remaining items sorted alphabetically.
 *
 * @param items - The items to sort.
 * @param isPrimary - Predicate to identify the primary item.
 * @param getSortKey - Function to get the sort key for alphabetical sorting.
 * @returns Sorted array with primary first.
 */
const sortWithPrimaryFirst = <T,>(
  items: T[],
  isPrimary: (item: T) => boolean,
  getSortKey: (item: T) => string,
): T[] => {
  const primaryIndex = items.findIndex(isPrimary);
  const result = [...items];

  let primary: T | undefined;
  if (primaryIndex >= 0) {
    [primary] = result.splice(primaryIndex, 1);
  }
  result.sort((a, b) => getSortKey(a).localeCompare(getSortKey(b)));
  if (primary) result.unshift(primary);

  return result;
};

/** Checks if a customer has a primary phone. */
const isPrimaryCustomer = (customer: ICustomer): boolean => customer.customerPhones.some((p) => p.primary);

/** Sorts customers with primary customer first, then alphabetically by name. */
const sortCustomers = (customers: ICustomer[]): ICustomer[] =>
  sortWithPrimaryFirst(customers, isPrimaryCustomer, (c) => c.name);

/** Sorts customers for email display with primary email first, filtered to those with email. */
const sortEmails = (customers: ICustomer[]): ICustomer[] =>
  sortWithPrimaryFirst(
    customers.filter((c) => c.email),
    (c) => c.primaryEmail,
    (c) => c.name,
  );

/** Gets all phones from customers, sorted with primary phones first. */
const getPhoneList = (customers: ICustomer[]): ICustomerPhone[] =>
  sortCustomers(customers).flatMap((customer) =>
    sortWithPrimaryFirst(
      customer.customerPhones,
      (p) => p.primary,
      (p) => p.number,
    ),
  );

/** Panel wrapper component with consistent header styling. */
const PanelWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="panel customers-panel">
    <div className="panel-heading">
      <h3 className="panel-title">Contact Info</h3>
    </div>
    {children}
  </div>
);

/** Renders a "Primary" badge label. */
const PrimaryBadge: React.FC = () => <span className="label label-primary"> Primary</span>;

/**
 * Customers Panel component displaying contact information for an account.
 *
 * @param props - The component props.
 * @returns The CustomersPanel component.
 */
export const CustomersPanel: React.FC<ICustomersPanelProps> = ({ accountID }) => {
  const { data, loading, error } = useCustomersPanelAccountQuery({
    client,
    variables: { accountID },
  });

  const customers = data?.account?.customers;

  const customerList = useMemo(() => sortCustomers(customers ?? []), [customers]);
  const emailList = useMemo(() => sortEmails(customers ?? []), [customers]);
  const phoneList = useMemo(() => getPhoneList(customers ?? []), [customers]);

  const hasMultipleCustomers = customerList.length > 1;
  const showPrimaryBadge = (isPrimary: boolean, listLength: number): boolean => isPrimary && listLength > 1;

  const primaryCustomerId = useMemo(
    () => customerList.find(isPrimaryCustomer)?.id ?? customerList[0]?.id,
    [customerList],
  );

  const editCustomersUrl = primaryCustomerId ? `/accounts/${accountID}/customers/${primaryCustomerId}/edit` : undefined;

  if (error) {
    return (
      <PanelWrapper>
        <div className="panel-body">
          <div className="text-danger">Failed to load customer information.</div>
        </div>
      </PanelWrapper>
    );
  }

  if (loading) {
    return (
      <PanelWrapper>
        <div className="panel-body">
          <Spinner />
        </div>
      </PanelWrapper>
    );
  }

  return (
    <PanelWrapper>
      <div className="panel-body">
        <div>
          <div>
            <strong>{customerList.length === 1 ? 'Customer:' : 'Customers:'}</strong>
          </div>
          <ol>
            {customerList.map((customer) => (
              <li key={customer.id}>{customer.name}</li>
            ))}
          </ol>
        </div>
        <div>
          <div>
            <strong>Email:</strong>
          </div>
          <ol>
            {emailList.map((customer) => (
              <li key={customer.id}>
                <a href={`mailto:${customer.email}`}>{customer.email}</a>
                {hasMultipleCustomers && <span> ({customer.name})</span>}
                {showPrimaryBadge(customer.primaryEmail, emailList.length) && <PrimaryBadge />}
              </li>
            ))}
          </ol>
        </div>
        <div>
          <div>
            <strong>Phone:</strong>
          </div>
          <ol>
            {phoneList.map((phone) => (
              <li key={phone.id}>
                <PhoneFormatter number={phone.number} />
                {hasMultipleCustomers && <span> ({phone.customerName})</span>}
                {showPrimaryBadge(phone.primary, phoneList.length) && <PrimaryBadge />}
              </li>
            ))}
          </ol>
        </div>
      </div>
      <div className="panel-footer text-right">
        <NavLinkButton to={`/accounts/${accountID}/preferences/edit`} kind="primary">
          Edit Preferences
        </NavLinkButton>{' '}
        {editCustomersUrl ? (
          <>
            {/* Pundit guard: AccountPolicy#customers_update? (accountCustomersUpdate, ENG-740) */}
            <NavLinkButton to={editCustomersUrl} kind="primary">
              Edit Customers
            </NavLinkButton>
          </>
        ) : (
          <button type="button" className="btn btn-primary" disabled title="No customers to edit">
            Edit Customers
          </button>
        )}
      </div>
    </PanelWrapper>
  );
};
